import {
  acceptStringFor,
  uploadableMimeTypesFor
} from '@/lib/constants/file-type-accept';

/**
 * File size and validation constants
 */

/** Maximum file upload size in bytes (20MB) */
export const MAX_FILE_SIZE = 20 * 1024 * 1024;

/** Maximum file upload size in megabytes (for display) */
export const MAX_FILE_SIZE_MB = 20;

/**
 * Maximum upload size for a will in the client-intake portal (50MB in bytes).
 * Mirrors the backend's `MAX_FILE_SIZE_WILL` (`backend/src/portal/dto/portal-upload-request.dto.ts`,
 * 52_428_800) — the portal's `documentType: 'will'` uploads allow the larger cap,
 * the one justified 50MB surface in the app (see `file-drop-zone/accept-presets.ts`).
 */
export const MAX_FILE_SIZE_WILL = 50 * 1024 * 1024;

/** Maximum will upload size in megabytes (for display) */
export const MAX_FILE_SIZE_WILL_MB = 50;

/**
 * Message shown when a user tries to upload an empty (0-byte) file. The most
 * common cause is a cloud-synced "online-only" placeholder (OneDrive,
 * SharePoint, Google Drive) that the browser reads as empty before it has been
 * downloaded locally. Catching it here avoids a silent upload that lands as a
 * present-but-unopenable document.
 */
export const EMPTY_FILE_MESSAGE =
  'This file is empty (0 bytes). If it lives in a cloud-synced folder (OneDrive, SharePoint, Google Drive), open it once so it downloads to your device, then upload it again.';

/** Maximum signature file size in bytes (2MB) */
export const MAX_SIGNATURE_SIZE = 2 * 1024 * 1024;

/** Maximum signature file size in megabytes (for display) */
export const MAX_SIGNATURE_SIZE_MB = 2;

/**
 * What the AI document-import door accepts — the `unknown-pending-ai-analysis` file type,
 * which is what both of this constant's surfaces (the AI import dialog and the create-matter
 * multi-document step) upload against.
 *
 * **Derived, not typed.** It used to be a hand-kept copy of the backend set carrying a comment
 * asking editors to keep it in sync, and it had drifted: it advertised
 * `application/vnd.ms-excel` (a legacy `.xls`), which no backend file type accepts at all, so
 * an `.xls` passed every client check and was refused after the S3 PUT. Reading it from
 * `file-type-accept.ts` means it cannot say something the backend disagrees with (VALE-209).
 *
 * `.eml`/`.msg` are included because this type unwraps a forwarded email into its attachments
 * server-side (LF-6125) rather than analysing the email itself — that is what
 * `uploadableMimeTypesFor` adds on top of the validated set.
 */
export const ALLOWED_FILE_TYPES: readonly string[] = uploadableMimeTypesFor(
  'unknown-pending-ai-analysis'
);

/**
 * Extensions with no reliable MIME type — a browser's file picker/drop frequently reports an
 * empty or generic `file.type` for these, so the pre-upload check needs the extension form too.
 *
 * Still hand-held rather than derived, and correctly so: this is a statement about what
 * *browsers* do, which the backend never sees and cannot be generated from. The `accept` string
 * below gets its own extension fallbacks from `acceptStringFor`.
 */
export const ALLOWED_FILE_EXTENSIONS_WITHOUT_RELIABLE_MIME = [
  '.eml',
  '.msg'
] as const;

/**
 * The AI import door's native `<input accept>` string.
 *
 * **Not `ALLOWED_FILE_TYPES.join(',')`, which is what both surfaces built until VALE-1150.**
 * That set is the *validator's*, and for an email-unwrapping type it includes the generic
 * `application/octet-stream` a real `.msg` arrives under — correct when checking a file the user
 * has already chosen, and wrong as a picker filter, where it matches every file the OS cannot
 * label. `acceptStringFor` drops the sniffer-only types and appends the `.eml`/`.msg`/`.heic`
 * extensions that a browser genuinely needs, which is what makes an email pickable at all.
 */
export const ALLOWED_FILE_TYPES_ACCEPT = acceptStringFor(
  'unknown-pending-ai-analysis'
);

/** Human-readable file type descriptions */
export const ALLOWED_FILE_TYPES_DESCRIPTION =
  'PDF, Word, Excel, Images (JPG, PNG), Text files, or a forwarded email (.eml/.msg)';
